import type { SkillDef } from '../sim/skillTypes';
import { area, circle, hit, party, self, skill, stage } from './classes/kit';

/**
 * Battle items are executed by the simulation as skills (so they replicate
 * and resolve like everything else). The game layer handles stock, per-
 * instance limits and cooldowns (cooldown = the item's cooldown).
 */

const throwable = (id: string, name: string, desc: string, hue: number, spec: Parameters<typeof area>[1]): SkillDef =>
  skill({
    id, name, description: desc, castType: 'point', cooldown: 30, mana: 0, maxRange: 12, hue, kind: 'monster',
    stages: [stage('Shoot', 0.3, [area(0.1, { ...spec, anchor: 'point', delay: spec.delay ?? 0.35 })], { cancelAt: 0.15 })],
    superArmor: 'none', stagger: 'none', positional: 'none',
  });

const heal = (id: string, name: string, pct: number): SkillDef =>
  skill({
    id, name, description: `Restore ${Math.round(pct * 100)}% HP.`, castType: 'normal', cooldown: 10, mana: 0, hue: 0xff5050, kind: 'monster',
    stages: [stage('Buff', 0.25, [hit(0.05, circle(0.4), 0, { friendly: true, healPct: pct, maxTargets: 1 })], { cancelAt: 0.1 })],
  });

export const BATTLE_ITEM_SKILLS: SkillDef[] = [
  heal('bi_potion_small', 'Healing Draught', 0.3),
  heal('bi_potion_major', 'Greater Healing Draught', 0.45),
  heal('bi_potion_elixir', 'Radiant Elixir', 0.6),
  throwable('bi_flame', 'Flame Grenade', 'Fire damage and a burning field.', 0xff6a2a, {
    shape: circle(3.5), duration: 10, tickInterval: 0.5, telegraph: 'none', vfx: 'pillar',
    hit: { shape: circle(3.5), coef: 0.6, element: 'fire', stagger: 'midHigh', status: [{ id: 'burn', duration: 3, value: 0.15 }] },
  }),
  throwable('bi_frost', 'Frost Grenade', 'Freezes non-boss foes.', 0x7ad8ff, {
    shape: circle(3.5), telegraph: 'none',
    hit: { shape: circle(3.5), coef: 1.2, element: 'water', stagger: 'midHigh', status: [{ id: 'freeze', duration: 4 }] },
  }),
  throwable('bi_flash', 'Flash Grenade', 'Stuns non-boss foes.', 0xffe28a, {
    shape: circle(3.5), telegraph: 'none',
    hit: { shape: circle(3.5), coef: 1.2, element: 'holy', stagger: 'high', status: [{ id: 'stun', duration: 4 }] },
  }),
  throwable('bi_whirl', 'Whirlwind Grenade', 'Launches foes; very high stagger.', 0x9affc8, {
    shape: circle(3.5), telegraph: 'none',
    hit: { shape: circle(3.5), coef: 1.0, stagger: 'max', weakPoint: 1, reaction: { kind: 'launch', power: 0.4, duration: 1 } },
  }),
  throwable('bi_dark', 'Shade Grenade', 'Target defence -20% for 20 s.', 0x9a50ff, {
    shape: circle(3.5), telegraph: 'none',
    hit: { shape: circle(3.5), coef: 1.0, element: 'dark', stagger: 'low', status: [{ id: 'sundered', duration: 20, value: -0.2 }] },
  }),
  throwable('bi_clay', 'Clay Grenade', 'Attack and move speed -40% for 20 s.', 0xa48a5c, {
    shape: circle(3.5), telegraph: 'none',
    hit: { shape: circle(3.5), coef: 1.0, element: 'earth', stagger: 'high', status: [{ id: 'clay', duration: 20 }] },
  }),
  throwable('bi_destruction', 'Sundering Bomb', 'Weak Point Lv. 3.', 0xffb040, {
    shape: circle(3), telegraph: 'none',
    hit: { shape: circle(3), coef: 1.5, weakPoint: 3, stagger: 'mid' },
  }),
  throwable('bi_corrosive', 'Corrosive Bomb', 'Weak point damage +1 level for 20 s.', 0x8fbf3a, {
    shape: circle(3), telegraph: 'none',
    hit: { shape: circle(3), coef: 0.6, status: [{ id: 'corroded', duration: 20 }] },
  }),
  throwable('bi_sleep', 'Slumber Bomb', 'Puts non-boss foes to sleep.', 0xb5a4ff, {
    shape: circle(4), telegraph: 'none',
    hit: { shape: circle(4), coef: 0.01, status: [{ id: 'sleep', duration: 12 }] },
  }),
  skill({
    id: 'bi_timestop', name: 'Stillwater Tonic', description: 'Invulnerable for 3 s.', castType: 'normal', cooldown: 30, mana: 0, hue: 0xe0e8ff, kind: 'monster',
    stages: [stage('Buff', 0.2, [self(0, { id: 'invuln', duration: 3 }, { id: 'root', duration: 3 })], { cancelAt: 0.1 })],
  }),
  skill({
    id: 'bi_panacea', name: 'Panacea', description: 'Removes harmful effects.', castType: 'normal', cooldown: 30, mana: 0, hue: 0x9aff9a, kind: 'monster',
    stages: [stage('Buff', 0.2, [self(0, { id: 'statusImmune', duration: 2 })], { cancelAt: 0.1 })],
  }),
  skill({
    id: 'bi_protect', name: 'Aegis Tonic', description: 'Shield of 15% max HP for 8 s.', castType: 'normal', cooldown: 30, mana: 0, hue: 0xbfe0ff, kind: 'monster',
    stages: [stage('Buff', 0.2, [hit(0, circle(0.4), 0, { friendly: true, shieldPct: 0.15, shieldDuration: 8, maxTargets: 1 })], { cancelAt: 0.1 })],
  }),
  skill({
    id: 'bi_march', name: 'Marching Banner', description: 'Party move speed +20% for 20 s.', castType: 'normal', cooldown: 30, mana: 0, hue: 0x9affc8, kind: 'monster',
    stages: [stage('Buff', 0.3, [party(0.1, 20, { id: 'haste', duration: 20, value: 0.2 })], { cancelAt: 0.15 })],
  }),
  skill({
    id: 'bi_campfire', name: 'Campfire', description: 'Party HP/MP regeneration for 30 s.', castType: 'normal', cooldown: 60, mana: 0, hue: 0xffa040, kind: 'monster',
    stages: [stage('Buff', 0.5, [party(0.2, 10, { id: 'regen', duration: 30, value: 0.02 }, { id: 'manaFlow', duration: 30, value: 0.02 })], { cancelAt: 0.3 })],
  }),
];
